import { EXTRACT_OCR_STATUS } from "core/consts";
import aiApi from "core/model/api/endpoints/extractDocs";
import { useActivityContext } from "core/model/utils/browser/ActivitySingleton";
import { getErrorMessage } from "core/model/utils/errors";
import { ExtractOCRStatus, ExtractOCRStatusResponse } from "core/types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const ACTIVE_POLL_INTERVAL = 3_000;
const INACTIVE_POLL_INTERVAL = 120_000;

export const useExtractOCRStatus = ({
  careseekerId,
  patientId,
  skip,
}: {
  careseekerId: number | undefined;
  patientId: number;
  skip: boolean;
}) => {
  const [result, setResult] = useState<ExtractOCRStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);

  const { active } = useActivityContext();

  const isAllProcessed = useMemo(() => {
    return result?.documents.every((document) =>
      (
        [
          EXTRACT_OCR_STATUS.EXTRACTED,
          EXTRACT_OCR_STATUS.FAILED,
        ] as ExtractOCRStatus[]
      ).includes(document.status),
    );
  }, [result]);

  const pollInterval = useMemo(
    () => (active ? ACTIVE_POLL_INTERVAL : INACTIVE_POLL_INTERVAL),
    [active],
  );

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  const fetchStatus = useCallback(async () => {
    try {
      if (skip) {
        return null;
      }

      if (!careseekerId || !patientId) {
        throw new Error("careseekerId or patientId is not defined");
      }

      const ocrResult = await aiApi.extract.ocrStatus({
        careseekerId,
        patientId,
      });

      if (!isMountedRef.current) return null;

      setResult(ocrResult);
      setError(null);

      return ocrResult;
    } catch (err) {
      if (!isMountedRef.current) return null;

      const message = getErrorMessage(err);
      console.error("error with extract ocr", message);
      setError(message);
      stopPolling();
      return null;
    }
  }, [careseekerId, patientId, skip, stopPolling]);

  useEffect(() => {
    if (skip || isAllProcessed) {
      return;
    }

    fetchStatus();

    const intervalId = setInterval(() => {
      fetchStatus();
    }, pollInterval);

    intervalRef.current = intervalId;

    return () => {
      clearInterval(intervalId);
    };
  }, [fetchStatus, skip, isAllProcessed]);

  const refetch = useCallback(async () => {
    await fetchStatus();
  }, [fetchStatus]);

  return {
    error,
    refetch,
    result,
  };
};
